import { useState, type ReactNode } from 'react';
import {
  DOCKED_PREVIEW_GAP_PX,
  useDockedPreviewBlock,
} from '../../shared/results_page/docked_preview.js';
import type { ResultsAsideFrame } from '../../shared/results_page/results_layout.js';
import type { MapViewControlsCopy } from '../../shared/results_page/results_map.js';
import type { RentalMapView } from '../models.js';
import { RentalMap, type RentalMapPinCopy, type RentalMapSearchArea } from './rental_map.js';
import { rentalAgencyKey, type RentalAgencyPin, type RentalMapOffer } from './rental_map_pins.js';
import { RentalMapPreview, type RentalMapPreviewCopy } from './rental_map_preview.js';
import type { RentalOfferCardCopy } from './rental_offer_card.js';

/** A copy do mapa da página: os controles de vista, o pino e a folha do carro
 *  selecionado do celular. */
export type RentalResultsMapCopy = Omit<MapViewControlsCopy, 'onExpand'> & {
  pin: RentalMapPinCopy;
  preview: RentalMapPreviewCopy;
};

export type RentalResultsMapAsideProps = {
  frame: ResultsAsideFrame;
  map: RentalMapView;
  /** A cena do mapa: as ofertas com todos os filtros MENOS o do pino — com
   *  ele, tocar num pino apagaria os outros e não daria para trocar de agência
   *  direto. */
  offers: readonly RentalMapOffer[];
  selectedOfferId: string | undefined;
  /** O pino que filtra a lista (`RentalAgencyPin.key`). */
  pinnedKey: string | undefined;
  /** Sem ele o pino é só leitura. */
  onPinnedKeyChange: ((key: string | undefined) => void) | undefined;
  copy: RentalResultsMapCopy;
  cardCopy: RentalOfferCardCopy;
  locked: boolean;
  onMapExpand: (() => void) | undefined;
  onSelect: (offerId: string) => void;
  /** Ver `RentalResultsPageProps.searchArea`. */
  searchArea: RentalMapSearchArea | undefined;
};

/**
 * O mapa no aside da página do carro, nas duas peles do layout. Ao LADO da
 * lista é o cartão arredondado com o expandir; no PALCO do celular (#3106) toma
 * a tela sem moldura, enquadra os pinos acima da folha e centra os controles
 * no mapa que sobra (`--hui-map-visible`), descontando a folha do carro
 * selecionado quando ela está aberta (`--hui-map-preview-block`, #3845).
 *
 * O toque no pino (#3995) filtra a lista pela agência de retirada; o segundo
 * toque no mesmo pino solta. No palco, filtrar leva a folha ao híbrido com a
 * lista filtrada — ou, se a agência tem uma oferta só, recolhe a folha e abre
 * a do carro selecionado (#5125) no pé do mapa, no molde da prévia do hotel.
 */
export function RentalResultsMapAside({
  frame,
  map,
  offers,
  selectedOfferId,
  pinnedKey,
  onPinnedKeyChange,
  copy,
  cardCopy,
  locked,
  onMapExpand,
  onSelect,
  searchArea,
}: Readonly<RentalResultsMapAsideProps>): ReactNode {
  const [previewId, setPreviewId] = useState<string>();
  const block = useDockedPreviewBlock();
  const onStage = frame.placement === 'stage';
  const { pin, preview, ...controls } = copy;
  // A folha só vive com o filtro do pino em pé e a oferta ainda na cena: o
  // `Redefinir` (ou outro filtro que a tire) a fecha sem efeito próprio.
  const previewOffer =
    onStage && pinnedKey !== undefined && previewId !== undefined
      ? offers.find(({ offer }) => offer.id === previewId && rentalAgencyKey(offer) === pinnedKey)
          ?.offer
      : undefined;
  const pressPin = (pressed: RentalAgencyPin) => {
    const next = pressed.key === pinnedKey ? undefined : pressed.key;
    onPinnedKeyChange?.(next);
    const [only, ...others] = pressed.offerIds;
    if (next === undefined || !onStage || only === undefined) {
      setPreviewId(undefined);
      return;
    }
    if (others.length === 0) {
      setPreviewId(only);
      frame.collapseSheet();
    } else {
      setPreviewId(undefined);
      frame.showList();
    }
  };
  const rentalMap = (
    <RentalMap
      map={map}
      offers={offers}
      {...(selectedOfferId ? { selectedOfferId } : {})}
      {...(pinnedKey ? { pinnedKey } : {})}
      {...(onPinnedKeyChange ? { onPinPress: pressPin } : {})}
      pinCopy={pin}
      {...(searchArea ? { searchArea } : {})}
      {...(onStage
        ? { overlays: { sheet: frame.sheetHeight }, controls }
        : { controls: { ...controls, ...(onMapExpand ? { onExpand: onMapExpand } : {}) } })}
    />
  );
  if (!onStage) {
    return (
      <div className="flex min-h-0 flex-1 overflow-hidden rounded-2xl [&>*]:h-full [&>*]:w-full">
        {rentalMap}
      </div>
    );
  }
  // No PALCO o mapa toma a tela sem moldura e sem o expandir — já é a tela. O
  // seletor pega só a `<section>` do mapa: a folha do carro é irmã dele e
  // senta no pé por conta própria.
  return (
    <div
      className="absolute inset-0 [&>section]:absolute [&>section]:inset-0 [&>section]:min-h-0 [&>section]:rounded-none [&>section]:border-0 [&_.iuk-results-map-view-controls]:top-[max(7.5rem,calc((var(--hui-map-visible)-var(--hui-map-preview-block,0px))/2))]"
      style={block.style}
    >
      {rentalMap}
      {previewOffer && (
        <RentalMapPreview
          offer={previewOffer}
          copy={preview}
          cardCopy={cardCopy}
          bottom={frame.sheetHeight + DOCKED_PREVIEW_GAP_PX}
          busy={locked}
          onSelect={onSelect}
          onClose={() => {
            setPreviewId(undefined);
          }}
          onResize={block.onDockedResize}
        />
      )}
    </div>
  );
}
